// assistant-batch-create.ts — pure helpers for the `create_items` batch tool.
// Lets the AI turn an attached plan/file into MANY entities created in ONE
// confirm step (instead of one proposal per item). Resolves project/goal links
// BY NAME — both to items already in state AND to items created in the same batch.
//
// Pure: applyBatchToState takes the current state + items and returns the next
// state (used inside the `addEntitiesBatch` action's functional mutate, so ids
// compose against the freshest state and only new items are marked dirty).

import type { Goal, Habit, LifeOsState, Note, Project, Task, TaskSubtask } from "../../types";
import {
  sanitizeNewGoal,
  sanitizeNewHabit,
  sanitizeNewNote,
  sanitizeNewProject,
  sanitizeNewTask,
} from "../domainItems";
import { markDirty } from "../lifeOsMutations";
import { makeId } from "../storage";
import { todayIso } from "../date";
import { num, safeDate, str, strArr } from "./assistant-tool-helpers";

// ── Types ─────────────────────────────────────────────────────────────────────

export type BatchKind = "task" | "project" | "goal" | "habit" | "note";

/** One normalised item to create. Links use NAMES (resolved at apply time). */
export interface BatchItem {
  kind: BatchKind;
  /** Task/goal/note title, or project/habit name. */
  title: string;
  description?: string;
  priority?: "low" | "medium" | "high";
  /** Task due date, or project/goal deadline (YYYY-MM-DD). */
  dueDate?: string;
  estimateMinutes?: number;
  subtasks?: string[];
  tags?: string[];
  /** Link a task/note to a project by name (existing or new in this batch). */
  projectName?: string;
  /** Link a task/note/habit, or a project, to a goal by name. */
  goalName?: string;
  area?: string;
  horizon?: "quarter" | "year" | "life";
  metric?: string;
  unit?: string;
  target?: number;
  targetPerWeek?: number;
  category?: "project" | "area" | "resource" | "archive";
}

const VALID_KINDS: BatchKind[] = ["task", "project", "goal", "habit", "note"];

// ── normalizeBatchItems ─────────────────────────────────────────────────────────

/** Validate + normalise raw tool args into safe BatchItem[]. Drops junk items. */
export function normalizeBatchItems(raw: unknown): BatchItem[] {
  const arr = Array.isArray(raw) ? raw : [];
  const out: BatchItem[] = [];
  for (const r of arr) {
    if (r === null || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    const kind = str(o.kind) as BatchKind;
    const title = str(o.title) || str(o.name);
    if (!VALID_KINDS.includes(kind) || !title) continue;

    const item: BatchItem = { kind, title };
    if (o.description !== undefined || o.body !== undefined) item.description = str(o.description ?? o.body);
    if (["low", "medium", "high"].includes(str(o.priority))) item.priority = str(o.priority) as BatchItem["priority"];
    if (o.dueDate !== undefined || o.deadline !== undefined) item.dueDate = safeDate(o.dueDate ?? o.deadline, "");
    if (o.estimateMinutes !== undefined) item.estimateMinutes = num(o.estimateMinutes, 30);
    const subs = strArr(o.subtasks);
    if (subs.length) item.subtasks = subs;
    const tags = strArr(o.tags);
    if (tags.length) item.tags = tags;
    if (o.projectName !== undefined) item.projectName = str(o.projectName);
    if (o.goalName !== undefined) item.goalName = str(o.goalName);
    if (o.area !== undefined) item.area = str(o.area);
    if (["quarter", "year", "life"].includes(str(o.horizon))) item.horizon = str(o.horizon) as BatchItem["horizon"];
    if (o.metric !== undefined) item.metric = str(o.metric);
    if (o.unit !== undefined) item.unit = str(o.unit);
    if (o.target !== undefined) item.target = num(o.target, 100);
    if (o.targetPerWeek !== undefined) item.targetPerWeek = num(o.targetPerWeek, 3);
    if (["project", "area", "resource", "archive"].includes(str(o.category))) {
      item.category = str(o.category) as BatchItem["category"];
    }
    out.push(item);
  }
  return out;
}

/** Short Vietnamese label per kind for the confirm card. */
export function kindLabel(kind: BatchKind): string {
  return { task: "Việc", project: "Dự án", goal: "Mục tiêu", habit: "Thói quen", note: "Ghi chú" }[kind];
}

// ── applyBatchToState ───────────────────────────────────────────────────────────

const key = (s?: string) => (s ?? "").trim().toLowerCase();

/**
 * Append all batch items to `current`, resolving project/goal links by name.
 * Order: goals → projects → tasks/habits/notes, so later items can link to
 * earlier ones created in the same batch. Each new item is marked dirty.
 */
export function applyBatchToState(current: LifeOsState, items: BatchItem[]): LifeOsState {
  const today = todayIso();
  let next = current;

  const projectIdByName = new Map<string, string>(current.projects.map((p) => [key(p.name), p.id]));
  const goalIdByName = new Map<string, string>(current.goals.map((g) => [key(g.title), g.id]));

  // Pass 1a — goals (no inbound links).
  for (const it of items) {
    if (it.kind !== "goal") continue;
    const id = makeId("goal");
    const goal: Omit<Goal, "id"> = sanitizeNewGoal({
      title: it.title,
      horizon: it.horizon ?? "quarter",
      metric: it.metric ?? "",
      current: 0,
      target: it.target ?? 100,
      deadline: it.dueDate || today,
      status: "active",
      ...(it.description ? { objective: it.description } : {}),
      ...(it.unit ? { unit: it.unit } : {}),
    });
    next = markDirty(next, "goals", id);
    next = { ...next, goals: [{ ...goal, id }, ...next.goals] };
    goalIdByName.set(key(it.title), id);
  }

  // Pass 1b — projects (may link to a goal).
  for (const it of items) {
    if (it.kind !== "project") continue;
    const id = makeId("project");
    const goalId = it.goalName ? goalIdByName.get(key(it.goalName)) : undefined;
    const project: Omit<Project, "id"> = sanitizeNewProject({
      name: it.title,
      area: it.area ?? "Chung",
      status: "active",
      deadline: it.dueDate || today,
      progress: 0,
      nextAction: "",
      ...(goalId ? { goalId } : {}),
    });
    next = markDirty(next, "projects", id);
    next = { ...next, projects: [{ ...project, id }, ...next.projects] };
    projectIdByName.set(key(it.title), id);
  }

  // Pass 2 — tasks, habits, notes (may link to project/goal).
  for (const it of items) {
    if (it.kind === "task") {
      const id = makeId("task");
      const projectId = it.projectName ? projectIdByName.get(key(it.projectName)) : undefined;
      const goalId = it.goalName ? goalIdByName.get(key(it.goalName)) : undefined;
      const subtasks: TaskSubtask[] = (it.subtasks ?? [])
        .map((t) => t.trim())
        .filter(Boolean)
        .map((t) => ({ id: makeId("sub"), title: t, done: false }));
      const task: Omit<Task, "id"> = sanitizeNewTask({
        title: it.title,
        dueDate: it.dueDate || today,
        status: "todo",
        priority: it.priority ?? "medium",
        estimateMinutes: it.estimateMinutes ?? 30,
        subtasks,
        ...(projectId ? { projectId } : {}),
        ...(goalId ? { goalId } : {}),
        ...(it.description ? { description: it.description } : {}),
        ...(it.tags?.length ? { tags: it.tags } : {}),
      });
      next = markDirty(next, "tasks", id);
      next = { ...next, tasks: [{ ...task, id }, ...next.tasks] };
    } else if (it.kind === "habit") {
      const id = makeId("habit");
      const linkedGoalId = it.goalName ? goalIdByName.get(key(it.goalName)) : undefined;
      const habit: Omit<Habit, "id" | "completions"> = sanitizeNewHabit({
        name: it.title,
        targetPerWeek: it.targetPerWeek ?? 3,
        color: "#6366f1",
        ...(linkedGoalId ? { linkedGoalId } : {}),
      });
      next = markDirty(next, "habits", id);
      next = { ...next, habits: [{ ...habit, id, completions: [] }, ...next.habits] };
    } else if (it.kind === "note") {
      const id = makeId("note");
      const linkedProjectId = it.projectName ? projectIdByName.get(key(it.projectName)) : undefined;
      const linkedGoalId = it.goalName ? goalIdByName.get(key(it.goalName)) : undefined;
      const note: Omit<Note, "id" | "updatedAt"> = sanitizeNewNote({
        title: it.title,
        body: it.description ?? "",
        tags: it.tags ?? [],
        pinned: false,
        attachments: [],
        ...(it.category ? { category: it.category } : {}),
        ...(linkedProjectId ? { linkedProjectId } : {}),
        ...(linkedGoalId ? { linkedGoalId } : {}),
      });
      next = markDirty(next, "notes", id);
      next = { ...next, notes: [{ ...note, id, updatedAt: new Date().toISOString() }, ...next.notes] };
    }
  }

  return next;
}
